import type { Porch } from '../types';
import { Builder, MAT } from '../engine/builder';
import { V3, v3, rng } from '../engine/math';

// The porch in metres: the house wall at z = WALL facing +z, the deck's top at DECK, the lawn at y = 0 in front.
export const WALL = -2.6, DECK = 0.45, DECK_X = 3.6;
/** the house front's half width: wider than the porch, so the view has room to turn before it reaches the ends */
export const HOUSE_X = 7.5;
/** how far the house runs back from its front; the roof's ridge runs across it, halfway back */
export const HOUSE_D = 8;
/** the roof's pitch, and its foot at the front wall (where the eave strip ends) */
const ROOF_A = 0.62, ROOF_Y = 3.52;
export const RIDGE_Y = ROOF_Y + (HOUSE_D / 2 - 0.1) * Math.tan(ROOF_A);
/** the bulb's centre: the lantern hangs from a bracket arm that reaches out of the wall */
export const LAMP: V3 = [0.4, 1.92, WALL + 0.42];
/** a spot standing low in the garden, a little left of the steps, turned on the house: the pumpkins' second, longer shadows climb the wall */
export const GARDEN_SPOT = { at: [-1.5, 0.3, 4.4] as V3, aim: [0.2, 1.1, WALL] as V3, col: [1.5, 1.65, 2.1] as V3 };
export const STEP_X = 0.85;
/** the door's middle, half width and height above the deck */
export const DOOR_X = -0.75, DOOR_W = 0.48, DOOR_H = 2.1;

/** the night and the house's lean are fixed: whatever a save says, these are used (the types keep the fields) */
export const PORCH_DEFAULT: Porch = {
  seed: 31, planks: [0.2, 0.15, 0.1], siding: [0.07, 0.1, 0.09], door: [0.32, 0.03, 0.02], trim: [0.5, 0.47, 0.4],
  lamp: { color: [1, 0.62, 0.3], brightness: 1, on: true }, candy: true, grass: true, mist: 1.0, jank: 0.6, moon: 0,
};

/** the height of whatever is underfoot at x, z: deck, steps or lawn */
export function heightAt(x: number, z: number): number {
  if (z <= 0.02 && z > WALL && Math.abs(x) < DECK_X) return DECK;
  if (Math.abs(x) < STEP_X) {
    if (z > 0 && z <= 0.32) return 0.3;
    if (z > 0.32 && z <= 0.64) return 0.15;
  }
  return 0;
}

/** the grass grows on the lawn, not under the deck, steps or path: bare boxes, x and z from and to */
export const BARE: [number, number, number, number][] = [
  [-DECK_X - 0.2, -10, DECK_X + 0.2, 0.05], [-0.62, 0, 0.62, 12], [-STEP_X - 0.05, 0, STEP_X + 0.05, 0.68],
  [-HOUSE_X - 0.1, WALL - HOUSE_D - 0.1, HOUSE_X + 0.1, WALL],
];

const vary = (c: V3, r: () => number, amt: number): V3 => {
  const k = 1 + (r() - 0.5) * 2 * amt;
  return [c[0] * k * (1 + (r() - 0.5) * amt * 0.5), c[1] * k, c[2] * k * (1 + (r() - 0.5) * amt * 0.5)];
};

export function buildPorch(p: Porch): { v: Float32Array; i: Uint32Array } {
  const b = new Builder(), r = rng(p.seed), J = p.jank;
  const wob = 0.006 * J, tip = 0.02 * J;
  const tilt = (): [number, number] => [(r() - 0.5) * tip, (r() - 0.5) * tip];
  const seed = () => (r() * 1e9) | 0;

  // lawn: the soil the grass grows from, stretching into the dark
  b.quad([-26, 0, 18], [26, 0, 18], [26, 0, WALL - HOUSE_D - 16], [-26, 0, WALL - HOUSE_D - 16], [0.02, 0.03, 0.012], MAT.soil);

  // deck planks, running out from the house, each its own board
  const pw = 0.145, gap = 0.009;
  for (let x = -DECK_X + pw / 2; x < DECK_X; x += pw + gap) {
    const len = -WALL + (r() - 0.5) * 0.03;
    b.box([x, DECK - 0.022 + (r() - 0.5) * 0.008 * J, WALL + len / 2 + 0.01], [pw / 2, 0.022, len / 2], vary(p.planks, r, 0.18), MAT.plank,
      { yaw: (r() - 0.5) * 0.012 * J, tilt: tilt(), wob, seed: seed() });
  }
  // the deck's front fascia and the dark under it
  b.box([0, DECK / 2, 0.03], [DECK_X + 0.05, DECK / 2, 0.025], p.trim, MAT.plain, { wob: wob * 2, seed: seed(), tilt: [0, 0.006 * J] });
  b.box([0, DECK / 2 - 0.02, -1.2], [DECK_X, DECK / 2 - 0.03, 1.2], [0.01, 0.01, 0.01], MAT.plain, { skip: 'y+' });

  // steps
  for (const [y, z0, z1] of [[0.3, 0.02, 0.34], [0.15, 0.32, 0.66]] as const) {
    b.box([0, y - 0.025, (z0 + z1) / 2], [STEP_X, 0.025, (z1 - z0) / 2], vary(p.planks, r, 0.12), MAT.plank, { tilt: tilt(), wob, seed: seed() });
    b.box([0, (y - 0.05) / 2, z1 - 0.02], [STEP_X - 0.02, (y - 0.05) / 2, 0.02], p.trim, MAT.plain, { wob, seed: seed() });
  }
  for (const s of [-1, 1]) b.box([s * (STEP_X + 0.03), 0.16, 0.34], [0.03, 0.16, 0.32], p.trim, MAT.plain, { wob, seed: seed(), tilt: tilt() });

  // the wall: lap siding, each board tipped out at its foot
  const sh = 0.165;
  for (let y = DECK; y < 3.4; y += sh - 0.012) {
    b.box([0, y + sh / 2, WALL - 0.02], [HOUSE_X, sh / 2, 0.012], vary(p.siding, r, 0.1), MAT.siding,
      { tilt: [-0.07 + (r() - 0.5) * 0.02 * J, (r() - 0.5) * 0.004 * J], wob: wob * 0.5, seed: seed() });
  }
  // the door: raised-panel, six panels, sagging a touch in its frame
  const dx = DOOR_X, dw = DOOR_W, dh = DOOR_H / 2, dz = WALL + 0.02, sag = 0.02 * J;
  const zf = dz + 0.02, dc = p.door, dd = v3.scale(dc, 0.62), dl = v3.scale(dc, 1.08);
  b.box([dx, DECK + dh, dz - 0.005], [dw, dh, 0.025], dd, MAT.door, { tilt: [0, sag], wob: wob * 0.5, seed: seed() });
  const door = (lx: number, ly: number, lz: number): V3 => [dx + lx + sag * (ly / dh) * 0.5, DECK + ly, zf + lz];
  // stiles, rails and the muntin between the panels
  for (const s of [-1, 1]) b.box(door(s * 0.41, dh, 0.011), [0.07, dh, 0.011], dc, MAT.door, { wob: wob * 0.3, seed: seed() });
  for (const [y0, y1] of [[0, 0.16], [0.92, 1.04], [1.5, 1.62], [1.98, 2.1]]) b.box(door(0, (y0 + y1) / 2, 0.01), [0.35, (y1 - y0) / 2, 0.01], dc, MAT.door, { wob: wob * 0.3, seed: seed() });
  for (const [y0, y1] of [[0.16, 0.92], [1.04, 1.5], [1.62, 1.98]]) {
    b.box(door(0, (y0 + y1) / 2, 0.01), [0.03, (y1 - y0) / 2, 0.01], dc, MAT.door, { wob: wob * 0.3, seed: seed() });
    for (const s of [-1, 1]) {
      const cx = s * 0.19, cy = (y0 + y1) / 2, hw = 0.145, hh = (y1 - y0) / 2 - 0.012;
      bevelled(b, door(cx + (r() - 0.5) * 0.004 * J, cy, 0), hw, hh, 0.026, 0.035, dl, MAT.door);
      // the sticking: a thin bead round the opening
      b.box(door(cx, y0 + 0.004, 0.022), [hw + 0.012, 0.004, 0.005], dc, MAT.door, { seed: seed() });
      b.box(door(cx, y1 - 0.004, 0.022), [hw + 0.012, 0.004, 0.005], dc, MAT.door, { seed: seed() });
    }
  }
  // brass: a round knob on a round plate, a knocker, a letterbox
  const brass: V3 = [0.9, 0.62, 0.2], kn = door(0.41, 0.98, 0.02);
  lathZ(b, kn, [[0, 0.004], [0.052, 0.002], [0.056, 0.006], [0.05, 0.012], [0.03, 0.02], [0.016, 0.022], [0.012, 0.03], [0.011, 0.045]], brass, MAT.brass, 20);
  const ball: [number, number][] = [];
  for (let k = 0; k <= 10; k++) { const a = Math.PI * (k / 10); ball.push([Math.sin(a) * 0.033, 0.04 + 0.033 * (1 - Math.cos(a))]); }
  lathZ(b, kn, ball, brass, MAT.brass, 20);
  const kk = door(0, 1.7, 0.02);
  lathZ(b, kk, [[0, 0], [0.04, 0.002], [0.045, 0.01], [0.03, 0.022], [0.02, 0.03], [0.0, 0.034]], brass, MAT.brass, 16);
  const ring: V3[] = [];
  for (let k = 0; k <= 20; k++) { const a = k / 20 * Math.PI * 2; ring.push([kk[0] + Math.sin(a) * 0.07, kk[1] - 0.075 + Math.cos(a) * 0.07 - 0.0, kk[2] + 0.05]); }
  b.tube(ring, ring.map(() => 0.009), () => brass, MAT.brass, 8);
  b.box(door(0, 1.27, 0.03), [0.12, 0.022, 0.01], brass, MAT.brass, { seed: seed() });
  b.box(door(0, 1.27, 0.04), [0.1, 0.012, 0.004], [0.01, 0.01, 0.01], MAT.metal, { seed: seed() });
  // its frame: casing with plinth blocks, a head with a cornice and dentils, the threshold
  const fz = dz + 0.01, ft = () => (r() - 0.5) * 0.012 * J;
  for (const s of [-1, 1]) {
    b.box([dx + s * (dw + 0.07), DECK + dh + 0.1, fz], [0.06, dh + 0.1, 0.032], p.trim, MAT.plain, { tilt: [0, ft()], wob, seed: seed() });
    b.box([dx + s * (dw + 0.015), DECK + dh, fz - 0.005], [0.014, dh, 0.045], v3.scale(p.trim, 0.8), MAT.plain, { seed: seed() });
    b.box([dx + s * (dw + 0.07), DECK + 0.11, fz + 0.012], [0.085, 0.11, 0.046], p.trim, MAT.plain, { tilt: [0, ft()], wob, seed: seed() });
    b.box([dx + s * (dw + 0.07), DECK + 0.23, fz + 0.012], [0.07, 0.012, 0.05], p.trim, MAT.plain, { seed: seed() });
    b.box([dx + s * (dw + 0.07), DECK + 2 * dh - 0.12, fz + 0.012], [0.075, 0.05, 0.044], p.trim, MAT.plain, { seed: seed() });
  }
  const top = DECK + 2 * dh + 0.1;
  b.box([dx, DECK + 2 * dh + 0.05, fz - 0.008], [dw + 0.01, 0.052, 0.03], v3.scale(p.trim, 0.75), MAT.plain, { seed: seed() });
  b.box([dx, top + 0.06, fz], [dw + 0.14, 0.06, 0.034], p.trim, MAT.plain, { tilt: [0, ft()], wob, seed: seed() });
  b.box([dx, top + 0.15, fz + 0.01], [dw + 0.17, 0.03, 0.05], p.trim, MAT.plain, { tilt: [0, ft()], wob, seed: seed() });
  b.box([dx, top + 0.21, fz + 0.025], [dw + 0.2, 0.018, 0.058], v3.scale(p.trim, 1.05), MAT.plain, { tilt: [0, ft()], wob, seed: seed() });
  b.box([dx, top + 0.26, fz + 0.012], [dw + 0.15, 0.025, 0.04], v3.scale(p.trim, 0.9), MAT.plain, { tilt: [-0.5, ft()], wob, seed: seed() });
  for (let k = -5; k <= 5; k++) b.box([dx + k * 0.1, top + 0.1, fz + 0.04], [0.025, 0.02, 0.025], v3.scale(p.trim, 0.85), MAT.plain, { seed: seed() });
  b.box([dx, DECK + 0.014, dz + 0.1], [dw + 0.1, 0.014, 0.1], v3.scale(p.trim, 0.9), MAT.plain, { tilt: [0, ft()], wob, seed: seed() });
  b.box([dx, DECK + 0.03, dz + 0.04], [dw, 0.016, 0.03], [0.18, 0.15, 0.1], MAT.metal, { seed: seed() });
  // a baseboard along the wall's foot
  b.box([0, DECK + 0.07, WALL + 0.003], [HOUSE_X, 0.07, 0.02], v3.scale(p.trim, 0.85), MAT.plain, { tilt: [0, 0.004 * J], wob: wob * 0.5, seed: seed() });
  // beyond the porch: the foundation under the siding, and the eave of the house's own roof along the top
  b.box([0, DECK / 2, WALL - 0.01], [HOUSE_X, DECK / 2, 0.03], [0.035, 0.033, 0.03], MAT.stone, { wob: wob * 2, seed: seed() });
  b.box([0, 3.42, WALL + 0.2], [HOUSE_X + 0.25, 0.04, 0.32], [0.03, 0.025, 0.03], MAT.roof, { tilt: [0.32, 0.006 * J], wob: wob * 3, seed: seed() });
  b.box([0, 3.33, WALL + 0.5], [HOUSE_X + 0.25, 0.07, 0.025], v3.scale(p.trim, 0.7), MAT.plain, { tilt: [0, 0.004 * J], wob: wob * 2, seed: seed() });
  // and a dark window either side, nobody home in those rooms
  for (const s of [-1, 1]) {
    const x = s * 5.7, y = DECK + 1.5, hw = 0.42, hh = 0.55;
    b.box([x, y, WALL + 0.004], [hw, hh, 0.01], [0.012, 0.014, 0.02], MAT.plain, { seed: seed() });
    b.box([x, y, WALL + 0.03], [0.02, hh, 0.02], p.trim, MAT.plain, { seed: seed() });
    b.box([x, y, WALL + 0.03], [hw, 0.02, 0.02], p.trim, MAT.plain, { seed: seed() });
    for (const t of [-1, 1]) {
      b.box([x + t * (hw + 0.05), y, WALL + 0.02], [0.05, hh + 0.05, 0.03], p.trim, MAT.plain, { tilt: tilt(), wob, seed: seed() });
      b.box([x, y + t * (hh + 0.05), WALL + 0.02 + (t < 0 ? 0.02 : 0)], [hw + 0.1, 0.05, 0.03 + (t < 0 ? 0.03 : 0)], p.trim, MAT.plain, { tilt: tilt(), wob, seed: seed() });
    }
  }
  // the rest of the house, so the view can go all the way round; its own dice, so the front stays as it was
  {
    const hr = rng(p.seed + 101), hseed = () => (hr() * 1e9) | 0;
    houseBody(b, p, hr, hseed, wob, () => [(hr() - 0.5) * tip, (hr() - 0.5) * tip]);
    // the deck's open sides
    for (const sx of [-1, 1]) b.box([sx * (DECK_X + 0.025), DECK / 2, WALL / 2], [0.025, DECK / 2, -WALL / 2], p.trim, MAT.plain, { wob: wob * 2, seed: hseed() });
  }
  // a mat
  b.box([dx, DECK + 0.006, WALL + 0.45], [0.42, 0.006, 0.26], [0.09, 0.05, 0.025], MAT.plain, { yaw: (r() - 0.5) * 0.15, wob: 0.004, seed: seed() });

  // the window: warm behind its curtains
  const wx = 1.95, wy = DECK + 1.55, ww = 0.55, wh = 0.6;
  b.box([wx, wy, WALL + 0.004], [ww, wh, 0.01], [1, 0.55, 0.22], MAT.window, { tilt: [0, 0.01 * J], seed: seed() });
  b.box([wx, wy, WALL + 0.03], [0.022, wh, 0.02], p.trim, MAT.plain, { seed: seed() });
  b.box([wx, wy, WALL + 0.03], [ww, 0.022, 0.02], p.trim, MAT.plain, { seed: seed() });
  for (const s of [-1, 1]) {
    b.box([wx + s * (ww + 0.05), wy, WALL + 0.02], [0.05, wh + 0.05, 0.03], p.trim, MAT.plain, { tilt: tilt(), wob, seed: seed() });
    b.box([wx, wy + s * (wh + 0.05), WALL + 0.02 + (s < 0 ? 0.02 : 0)], [ww + 0.1, 0.05, 0.03 + (s < 0 ? 0.03 : 0)], p.trim, MAT.plain, { tilt: tilt(), wob, seed: seed() });
  }

  // the porch lamp: a bracket out of the wall, a chain, and an old cage lantern round a filament bulb
  lantern(b, seed, p);

  // posts and the roof they hold up
  const posts: V3[] = [[-DECK_X + 0.15, 0, -0.12], [DECK_X - 0.15, 0, -0.12]];
  for (const q of posts) {
    b.box([q[0], DECK + 1.3, q[2]], [0.07, 1.3, 0.07], p.trim, MAT.plain, { tilt: [(r() - 0.5) * 0.04 * J, (r() - 0.5) * 0.04 * J], wob: wob * 1.5, seed: seed() });
    b.box([q[0], DECK + 0.06, q[2]], [0.1, 0.06, 0.1], p.trim, MAT.plain, { wob, seed: seed() });
  }
  b.box([0, 3.12, WALL / 2 + 0.05], [DECK_X + 0.25, 0.04, -WALL / 2 + 0.3], v3.scale(p.trim, 0.8), MAT.plain, { tilt: [0, 0.008 * J], wob: wob * 2, seed: seed() });
  b.box([0, 3.06, 0.03], [DECK_X + 0.3, 0.12, 0.04], p.trim, MAT.plain, { tilt: [0, -0.01 * J], wob: wob * 2, seed: seed() });
  b.box([0, 3.42, WALL / 2], [DECK_X + 0.4, 0.04, -WALL / 2 + 0.55], [0.03, 0.025, 0.03], MAT.roof, { tilt: [0.32, 0.012 * J], wob: wob * 3, seed: seed() });

  // railings either side of the steps
  for (const s of [-1, 1]) {
    const x0 = s * (STEP_X + 0.05), x1 = posts[s < 0 ? 0 : 1][0], z = -0.1, mid = (x0 + x1) / 2, half = Math.abs(x1 - x0) / 2;
    b.box([mid, DECK + 0.95, z], [half, 0.035, 0.04], p.trim, MAT.rail, { tilt: [0, (r() - 0.5) * 0.03 * J], wob, seed: seed() });
    b.box([mid, DECK + 0.1, z], [half, 0.025, 0.03], p.trim, MAT.rail, { tilt: [0, (r() - 0.5) * 0.02 * J], wob, seed: seed() });
    b.box([x0, DECK + 0.5, z], [0.05, 0.5, 0.05], p.trim, MAT.post, { tilt: tilt(), wob, seed: seed() });
    for (let x = Math.min(x0, x1) + 0.12; x < Math.max(x0, x1) - 0.06; x += 0.13) {
      b.box([x, DECK + 0.52, z], [0.016, 0.4, 0.016], p.trim, MAT.post, { tilt: [(r() - 0.5) * 0.06 * J, (r() - 0.5) * 0.08 * J], wob: wob * 0.5, seed: seed() });
    }
  }

  // the path: flags of stone, each its own size and turn
  for (let z = 0.8; z < 11; z += 0.62 + r() * 0.1) {
    b.box([(r() - 0.5) * 0.12, 0.015, z], [0.4 + r() * 0.12, 0.02, 0.26 + r() * 0.06], vary([0.06, 0.06, 0.065], r, 0.25), MAT.stone,
      { yaw: (r() - 0.5) * 0.3, tilt: [(r() - 0.5) * 0.03, (r() - 0.5) * 0.03], wob: 0.02, seed: seed() });
  }

  // bushes either side of the porch
  for (const s of [-1, 1]) for (let k = 0; k < 4; k++) {
    const at: V3 = [s * (DECK_X + 0.6 + r() * 0.7), 0, -0.4 - r() * 1.6], rad = 0.4 + r() * 0.35, ph = r() * 9;
    const prof: [number, number][] = [];
    for (let i = 0; i <= 8; i++) { const a = Math.PI * i / 8; prof.push([Math.sin(a) * rad, (1 - Math.cos(a)) * rad * 0.8]); }
    b.lathe(at, prof, () => vary([0.02, 0.04, 0.015], r, 0.3), MAT.leaf, 14, a => 1 + 0.18 * Math.sin(5 * a + ph) + 0.1 * Math.sin(11 * a + ph * 2));
  }

  // fallen leaves, on the lawn and blown onto the deck
  const leafCols: V3[] = [[0.5, 0.12, 0.02], [0.35, 0.2, 0.03], [0.25, 0.05, 0.02], [0.45, 0.3, 0.05]];
  for (let k = 0; k < 260; k++) {
    const onDeck = k < 60;
    const x = onDeck ? (r() - 0.5) * 2 * DECK_X : (r() - 0.5) * 12, z = onDeck ? WALL + r() * -WALL : r() * 9;
    const y = heightAt(x, z) + 0.004 + r() * 0.004, a = r() * Math.PI * 2, s = 0.035 + r() * 0.03, c = leafCols[(r() * 4) | 0];
    const u: V3 = [Math.cos(a) * s, (r() - 0.5) * 0.01, Math.sin(a) * s], w: V3 = [-Math.sin(a) * s * 0.6, 0, Math.cos(a) * s * 0.6];
    const o: V3 = [x, y, z];
    b.quad(v3.sub(o, u), v3.add(o, w), v3.add(o, u), v3.sub(o, w), c, MAT.leaf);
  }

  if (p.candy) candyBucket(b, [1.35, DECK, -0.55], r);
  return b.data();
}


/**
 * Behind the front: two sides with the gable in each, the back with its own door, a pitched roof of shingle courses with
 * the ridge running across, a chimney. The front wall and its eave strip are drawn above; this meets them.
 */
function houseBody(b: Builder, p: Porch, r: () => number, seed: () => number, wob: number, tilt: () => [number, number]) {
  const X = HOUSE_X, zb = WALL - HOUSE_D, zr = WALL - HOUSE_D / 2, t = Math.tan(ROOF_A), sh = 0.165, J = p.jank;
  // the sides: lap siding running back from the front, rising into the gable as it narrows under the roof
  for (const s of [-1, 1]) {
    for (let y = DECK; y < RIDGE_Y - 0.15; y += sh - 0.012) {
      const half = Math.min(HOUSE_D / 2, (RIDGE_Y - 0.06 - y - sh) / t);
      if (half < 0.1) break;
      b.box([s * (X + 0.012), y + sh / 2, zr], [0.012, sh / 2, half], vary(p.siding, r, 0.1), MAT.siding,
        { tilt: [(r() - 0.5) * 0.004 * J, s * (0.07 + (r() - 0.5) * 0.02 * J)], wob: wob * 0.5, seed: seed() });
    }
    // behind the boards' stepped ends, the gable's own flat triangle
    const gx = s * (X - 0.004), g0 = ROOF_Y - 0.1 * t - 0.02, a: V3 = [gx, g0, WALL], c: V3 = [gx, g0, zb], top: V3 = [gx, RIDGE_Y - 0.04, zr];
    if (s > 0) b.quad(a, c, top, top, v3.scale(p.siding, 0.9), MAT.siding); else b.quad(c, a, top, top, v3.scale(p.siding, 0.9), MAT.siding);
    b.box([s * X, DECK / 2, zr], [0.03, DECK / 2, HOUSE_D / 2], [0.035, 0.033, 0.03], MAT.stone, { wob: wob * 2, seed: seed() });
    // corner boards, front and back
    for (const z of [WALL - 0.02, zb]) b.box([s * (X + 0.03), (DECK + ROOF_Y) / 2 - 0.05, z], [0.06, (ROOF_Y - DECK) / 2 - 0.05, 0.06], p.trim, MAT.plain, { tilt: tilt(), wob, seed: seed() });
    // two windows down each side and a small one high in the gable, lit: somebody up there after all
    for (const z of [WALL - 2.1, WALL - 5.9]) houseWindow(b, p, r, seed, wob, [s * (X + 0.06), DECK + 1.5, z], s * Math.PI / 2, 0.42, 0.55, s < 0 ? z < zr : z > zr);
    houseWindow(b, p, r, seed, wob, [s * (X + 0.06), RIDGE_Y - 1.55, zr], s * Math.PI / 2, 0.22, 0.28, s < 0);
  }
  // the back: siding up to the eave, and the foundation under it
  for (let y = DECK; y < ROOF_Y - 0.25; y += sh - 0.012) {
    b.box([0, y + sh / 2, zb - 0.012], [X + 0.02, sh / 2, 0.012], vary(p.siding, r, 0.1), MAT.siding,
      { tilt: [0.07 + (r() - 0.5) * 0.02 * J, (r() - 0.5) * 0.004 * J], wob: wob * 0.5, seed: seed() });
  }
  b.box([0, DECK / 2, zb + 0.01], [X, DECK / 2, 0.03], [0.035, 0.033, 0.03], MAT.stone, { wob: wob * 2, seed: seed() });
  for (const x of [-5.2, -2.6, 3.2, 5.6]) houseWindow(b, p, r, seed, wob, [x, DECK + 1.5, zb - 0.06], Math.PI, 0.42, 0.55, false);
  // the back door, plainer than the front, on a stone step
  const bx = 0.6, bz = zb - 0.03;
  b.box([bx, DECK + 1.0, bz + 0.01], [0.44, 1.0, 0.025], v3.scale(p.door, 0.7), MAT.door, { tilt: [0, 0.01 * J], wob: wob * 0.5, seed: seed() });
  for (const s of [-1, 1]) b.box([bx + s * 0.5, DECK + 1.05, bz - 0.01], [0.06, 1.05, 0.03], p.trim, MAT.plain, { tilt: tilt(), wob, seed: seed() });
  b.box([bx, DECK + 2.14, bz - 0.01], [0.58, 0.06, 0.04], p.trim, MAT.plain, { tilt: tilt(), wob, seed: seed() });
  b.box([bx + 0.32, DECK + 1.0, bz - 0.04], [0.02, 0.02, 0.025], [0.5, 0.4, 0.2], MAT.brass, { seed: seed() });
  b.box([bx, DECK / 2 + 0.02, bz - 0.3], [0.6, DECK / 2 + 0.02, 0.3], [0.05, 0.048, 0.045], MAT.stone, { wob: 0.01, tilt: tilt(), seed: seed() });
  b.box([bx, DECK / 4, bz - 0.75], [0.6, DECK / 4, 0.18], [0.05, 0.048, 0.045], MAT.stone, { wob: 0.01, tilt: tilt(), seed: seed() });

  // the roof: two slopes meeting at the ridge, each a dark deck under courses of shingles that overlap down the slope
  const over = 0.38, run = HOUSE_D / 2 + 0.45, L = run / Math.cos(ROOF_A), roof: V3 = [0.03, 0.025, 0.03];
  for (const s of [1, -1]) {
    // s = 1 the front slope, falling toward +z; -1 the back
    const foot: V3 = [0, RIDGE_Y - run * t, zr + s * run];
    const along = (d: number, up = 0): V3 => [0, foot[1] + d * Math.sin(ROOF_A) + up * Math.cos(ROOF_A), foot[2] - s * (d * Math.cos(ROOF_A) - up * Math.sin(ROOF_A))];
    b.box(along(L / 2, -0.05), [X + over, 0.03, L / 2], roof, MAT.roof, { tilt: [s * ROOF_A, 0], seed: seed() });
    for (let d = 0.12; d < L - 0.05; d += 0.2) {
      b.box(along(d, 0.0), [X + over + (r() - 0.5) * 0.04, 0.022, 0.13], vary([0.035, 0.03, 0.034], r, 0.25), MAT.roof,
        { tilt: [s * (ROOF_A + 0.05 + (r() - 0.5) * 0.02 * J), (r() - 0.5) * 0.004 * J], wob: wob * 2, seed: seed() });
    }
    // the bargeboards down each gable's edge
    for (const e of [-1, 1]) {
      const m = along(L / 2, -0.12);
      b.box([e * (X + over - 0.02), m[1], m[2]], [0.03, 0.11, L / 2], v3.scale(p.trim, 0.8), MAT.plain, { tilt: [s * ROOF_A, 0], wob, seed: seed() });
    }
  }
  b.box([0, RIDGE_Y + 0.06, zr], [X + over, 0.05, 0.12], [0.025, 0.02, 0.025], MAT.roof, { wob: wob * 2, seed: seed() });
  // a chimney through the back slope, brick-dark, with a cap and two pots
  const cx = -3.4, cz = zr - 1.1, ch = RIDGE_Y + 0.9;
  b.box([cx, ch / 2 + 2, cz], [0.38, ch / 2 - 2, 0.32], [0.07, 0.03, 0.022], MAT.stone, { wob: 0.01, tilt: [0, 0.01 * J], seed: seed() });
  b.box([cx, ch + 0.04, cz], [0.44, 0.05, 0.38], [0.05, 0.045, 0.04], MAT.stone, { wob: 0.006, seed: seed() });
  for (const dx of [-0.16, 0.16]) b.lathe([cx + dx, ch + 0.08, cz], [[0.08, 0], [0.085, 0.05], [0.07, 0.24], [0.08, 0.26], [0.06, 0.27]], () => [0.12, 0.05, 0.03], MAT.stone, 10);

  // a few bushes along the sides and the back, and leaves blown round there too
  for (let k = 0; k < 9; k++) {
    const side = k < 6, sx = k % 2 ? 1 : -1;
    const at: V3 = side ? [sx * (X + 0.6 + r() * 0.5), 0, WALL - 0.8 - r() * (HOUSE_D - 1.4)] : [(r() - 0.5) * 2 * (X - 1), 0, zb - 0.6 - r() * 0.4];
    const rad = 0.35 + r() * 0.35, ph = r() * 9, prof: [number, number][] = [];
    for (let i = 0; i <= 8; i++) { const a = Math.PI * i / 8; prof.push([Math.sin(a) * rad, (1 - Math.cos(a)) * rad * 0.8]); }
    b.lathe(at, prof, () => vary([0.02, 0.04, 0.015], r, 0.3), MAT.leaf, 14, a => 1 + 0.18 * Math.sin(5 * a + ph) + 0.1 * Math.sin(11 * a + ph * 2));
  }
  const leafCols: V3[] = [[0.5, 0.12, 0.02], [0.35, 0.2, 0.03], [0.25, 0.05, 0.02], [0.45, 0.3, 0.05]];
  for (let k = 0; k < 160; k++) {
    const x = (r() - 0.5) * 2 * (X + 4), z = WALL + 1 - r() * (HOUSE_D + 6);
    if (Math.abs(x) < X + 0.1 && z > zb - 0.1 && z < WALL + 0.1) continue;
    const a = r() * Math.PI * 2, sz = 0.035 + r() * 0.03, c = leafCols[(r() * 4) | 0];
    const u: V3 = [Math.cos(a) * sz, (r() - 0.5) * 0.01, Math.sin(a) * sz], w: V3 = [-Math.sin(a) * sz * 0.6, 0, Math.cos(a) * sz * 0.6], o: V3 = [x, 0.005 + r() * 0.004, z];
    b.quad(v3.sub(o, u), v3.add(o, w), v3.add(o, u), v3.sub(o, w), c, MAT.leaf);
  }
}

/** a sash window on a wall: `at` just proud of the siding, `yaw` turning the window's +z out of the wall; dark, or lit dim */
function houseWindow(b: Builder, p: Porch, r: () => number, seed: () => number, wob: number, at: V3, yaw: number, hw: number, hh: number, lit: boolean) {
  const cy = Math.cos(yaw), sy = Math.sin(yaw);
  const o = (x: number, y: number, z: number): V3 => [at[0] + x * cy + z * sy, at[1] + y, at[2] - x * sy + z * cy];
  const tl = (): [number, number] => [(r() - 0.5) * 0.02 * p.jank, (r() - 0.5) * 0.02 * p.jank];
  b.box(o(0, 0, -0.026), [hw, hh, 0.01], lit ? [0.8, 0.44, 0.17] : [0.012, 0.014, 0.02], lit ? MAT.window : MAT.plain, { yaw, seed: seed() });
  b.box(o(0, 0, 0), [0.02, hh, 0.02], p.trim, MAT.plain, { yaw, seed: seed() });
  b.box(o(0, 0, 0), [hw, 0.02, 0.02], p.trim, MAT.plain, { yaw, seed: seed() });
  for (const t of [-1, 1]) {
    b.box(o(t * (hw + 0.05), 0, -0.01), [0.05, hh + 0.05, 0.03], p.trim, MAT.plain, { yaw, tilt: tl(), wob, seed: seed() });
    b.box(o(0, t * (hh + 0.05), -0.01 + (t < 0 ? 0.02 : 0)), [hw + 0.1, 0.05, 0.03 + (t < 0 ? 0.03 : 0)], p.trim, MAT.plain, { yaw, tilt: tl(), wob, seed: seed() });
  }
}

/** a lathe turned about the z axis (profile [radius, distance out from the wall]), standing on `at` */
function lathZ(b: Builder, at: V3, profile: [number, number][], col: V3, mat: number, seg = 16) {
  const t = new Builder();
  t.lathe([0, 0, 0], profile.map(([rr, d]) => [rr, d] as [number, number]), () => col, mat, seg);
  const base = b.count;
  for (let k = 0; k < t.count; k++) {
    const o = k * 10, [x, y, z, nx, ny, nz] = t.v.slice(o, o + 6);
    b.vert([at[0] + x, at[1] - z, at[2] + y], [nx, -nz, ny], col, mat);
  }
  for (const i of t.i) b.i.push(base + i);
}

/** a raised panel with sloped edges: base rectangle hw x hh at `c`, rising `d` to a flat top inset by `bev` */
function bevelled(b: Builder, c: V3, hw: number, hh: number, d: number, bev: number, col: V3, mat: number) {
  const base: V3[] = [[-hw, -hh, 0], [hw, -hh, 0], [hw, hh, 0], [-hw, hh, 0]].map(q => [c[0] + q[0], c[1] + q[1], c[2]] as V3);
  const top: V3[] = [[-hw + bev, -hh + bev, d], [hw - bev, -hh + bev, d], [hw - bev, hh - bev, d], [-hw + bev, hh - bev, d]].map(q => [c[0] + q[0], c[1] + q[1], c[2] + q[2]] as V3);
  for (let i = 0; i < 4; i++) b.quad(base[i], base[(i + 1) % 4], top[(i + 1) % 4], top[i], col, mat);
  b.quad(top[0], top[1], top[2], top[3], v3.scale(col, 1.03), mat);
}

function lantern(b: Builder, seed: () => number, p: Porch) {
  const L = LAMP, iron: V3 = [0.035, 0.032, 0.028], brass: V3 = [0.9, 0.62, 0.2];
  const hot: V3 = p.lamp.on ? v3.scale(p.lamp.color, 1.25) : [0.04, 0.035, 0.03];
  const ay = L[1] + 0.3;            // the arm's height
  // wall plate and a scroll bracket
  b.box([L[0], ay - 0.09, WALL + 0.012], [0.04, 0.2, 0.012], iron, MAT.metal, { seed: seed() });
  for (const dy of [0.08, -0.28]) b.box([L[0], ay + dy, WALL + 0.026], [0.014, 0.014, 0.012], brass, MAT.brass, { seed: seed() });
  const arm: V3[] = [];
  for (let k = 0; k <= 12; k++) { const t = k / 12; arm.push([L[0], ay + 0.015 * Math.sin(t * Math.PI) * 0 - 0.012 * t * t, WALL + 0.03 + (L[2] - WALL - 0.03) * t]); }
  b.tube(arm, arm.map(() => 0.012), () => iron, MAT.metal, 7);
  // the diagonal brace and a scroll under it
  const brace: V3[] = [];
  for (let k = 0; k <= 10; k++) { const t = k / 10; brace.push([L[0], ay - 0.26 + 0.2 * Math.pow(t, 1.4) + 0.0, WALL + 0.03 + 0.2 * t]); }
  b.tube(brace, brace.map(() => 0.008), () => iron, MAT.metal, 6);
  const scroll: V3[] = [];
  for (let k = 0; k <= 22; k++) { const t = k / 22, a = t * Math.PI * 2.6 + 0.3, rr = 0.05 * (1 - t * 0.8); scroll.push([L[0], ay - 0.07 + Math.sin(a) * rr - 0.01, WALL + 0.05 + 0.1 + Math.cos(a) * rr - 0.0]); }
  b.tube(scroll, scroll.map(() => 0.0065), () => iron, MAT.metal, 5);
  const tip: V3 = arm[arm.length - 1];
  b.box([tip[0], tip[1], tip[2] + 0.002], [0.016, 0.016, 0.02], brass, MAT.brass, { seed: seed() });
  // hook and chain
  const top = L[1] + 0.22;
  for (let y = tip[1] - 0.03, k = 0; y > top + 0.035; y -= 0.022, k++) {
    b.box([L[0], y, L[2]], k % 2 ? [0.003, 0.014, 0.0065] : [0.0065, 0.014, 0.003], iron, MAT.metal, { seed: seed() });
  }
  const ringV: V3[] = [];
  for (let k = 0; k <= 12; k++) { const a = k / 12 * Math.PI * 2; ringV.push([L[0] + Math.cos(a) * 0.022, top + 0.03 + Math.sin(a) * 0.022, L[2]]); }
  b.tube(ringV, ringV.map(() => 0.004), () => iron, MAT.metal, 5);
  // cap, finial and vent
  b.lathe([L[0], L[1] + 0.12, L[2]], [[0.085, 0], [0.088, 0.008], [0.075, 0.016], [0.06, 0.035], [0.04, 0.062], [0.032, 0.07], [0.04, 0.074], [0.028, 0.082], [0.012, 0.09], [0.0, 0.092]], () => iron, MAT.metal, 16);
  b.lathe([L[0], L[1] + 0.12, L[2]], [[0.0, 0.2 - 0.12], [0.014, 0.2 - 0.118], [0.0, 0.2 - 0.108]], () => brass, MAT.brass, 8);
  b.lathe([L[0], L[1] + 0.114, L[2]], [[0.082, 0], [0.087, 0.006], [0.082, 0.012]], () => brass, MAT.brass, 16);
  // foot: a dished base the cage stands on
  b.lathe([L[0], L[1] - 0.15, L[2]], [[0.0, 0.0], [0.07, 0.0], [0.088, 0.012], [0.082, 0.026], [0.07, 0.034], [0.03, 0.04], [0.0, 0.04]], () => iron, MAT.metal, 16);
  b.lathe([L[0], L[1] - 0.115, L[2]], [[0.082, 0], [0.086, 0.006], [0.082, 0.012]], () => brass, MAT.brass, 16);
  // the cage: six bars and two hoops
  const n = 6, bar = 0.074;
  for (let k = 0; k < n; k++) {
    const a = k / n * Math.PI * 2 + 0.2;
    b.box([L[0] + Math.cos(a) * bar, L[1] - 0.01, L[2] + Math.sin(a) * bar], [0.0045, 0.115, 0.0045], iron, MAT.metal, { yaw: -a, seed: seed() });
  }
  for (const dy of [-0.1, 0.0, 0.1]) {
    const hoop: V3[] = [];
    for (let k = 0; k <= 18; k++) { const a = k / 18 * Math.PI * 2; hoop.push([L[0] + Math.cos(a) * bar, L[1] + dy, L[2] + Math.sin(a) * bar]); }
    b.tube(hoop, hoop.map(() => 0.0042), () => iron, MAT.metal, 4);
  }
  // the glass: a chimney round the bulb, drawn see-through
  b.lathe([L[0], L[1] - 0.112, L[2]], [[0.066, 0], [0.068, 0.1], [0.066, 0.215]], () => [0.6, 0.7, 0.75], MAT.glass, 18);
  // bulb: brass screw base, a pear of glass, and the filament inside
  b.lathe([L[0], L[1] - 0.112, L[2]], [[0.0, 0.0], [0.02, 0.0], [0.02, 0.02], [0.0235, 0.027], [0.02, 0.034], [0.0235, 0.04]], () => brass, MAT.brass, 12);
  const pear: [number, number][] = [[0.0125, 0.04], [0.02, 0.05], [0.036, 0.075], [0.044, 0.11], [0.04, 0.145], [0.026, 0.172], [0.009, 0.186], [0.0, 0.19]];
  b.lathe([L[0], L[1] - 0.112, L[2]], pear, () => [0.7, 0.55, 0.3], MAT.glass, 16);
  for (const s of [-1, 1]) b.tube([[L[0] + s * 0.004, L[1] - 0.075, L[2]], [L[0] + s * 0.012, L[1] - 0.012, L[2]]], [0.0016, 0.0013], () => iron, MAT.metal, 4);
  const fil: V3[] = [];
  for (let k = 0; k <= 14; k++) { const t = k / 14; fil.push([L[0] + (t - 0.5) * 0.024, L[1] - 0.012 + (k % 2 ? 0.014 : 0), L[2]]); }
  b.tube(fil, fil.map(() => 0.0024), () => hot, MAT.filament, 5);
}

/** the plastic jack-o-lantern pail, heaped with sweets */
function candyBucket(b: Builder, at: V3, r: () => number) {
  const R = 0.14, H = 0.2;
  const prof: [number, number][] = [[0, 0.002], [R * 0.82, 0.004], [R * 0.9, 0.02], [R, H * 0.55], [R * 1.02, H * 0.92], [R * 1.05, H], [R * 0.98, H]];
  b.lathe(at, prof, () => [0.75, 0.16, 0.0], MAT.plastic, 24, a => 1 + 0.04 * Math.abs(Math.sin(4 * a)));
  // the handle
  const spine: V3[] = [];
  for (let k = 0; k <= 12; k++) { const a = Math.PI * k / 12; spine.push([at[0] + Math.cos(a) * R * 1.02, at[1] + H + Math.sin(a) * 0.12, at[2] + 0.01]); }
  b.tube(spine, spine.map(() => 0.008), () => [0.05, 0.05, 0.05], MAT.plastic, 6);
  // the heap
  const cols: V3[] = [[0.6, 0.02, 0.02], [0.02, 0.15, 0.5], [0.8, 0.6, 0.02], [0.05, 0.4, 0.05], [0.5, 0.05, 0.4], [0.9, 0.9, 0.85]];
  for (let k = 0; k < 34; k++) {
    const a = r() * Math.PI * 2, d = Math.sqrt(r()) * R * 0.85, c = cols[(r() * cols.length) | 0];
    const y = at[1] + H - 0.01 + (1 - d / R) * 0.05 + r() * 0.02;
    b.box([at[0] + Math.cos(a) * d, y, at[2] + Math.sin(a) * d], [0.022, 0.012, 0.012 + r() * 0.01], c, MAT.plastic, { yaw: r() * 6, tilt: [r() - 0.5, r() - 0.5], seed: (r() * 1e9) | 0 });
  }
  // a couple spilled on the boards
  for (let k = 0; k < 3; k++) {
    b.box([at[0] + 0.2 + r() * 0.2, at[1] + 0.012, at[2] + 0.1 + r() * 0.15], [0.022, 0.011, 0.014], cols[(r() * cols.length) | 0], MAT.plastic, { yaw: r() * 6, seed: (r() * 1e9) | 0 });
  }
}

/** the candles or LEDs inside a pumpkin, in pumpkin space: their positions and their props */
export function lightRig(kind: string, count: number, floorY: number, radius: number): { at: V3[]; v: Float32Array; i: Uint32Array } {
  const b = new Builder(), at: V3[] = [];
  const n = kind === 'candles' ? Math.max(2, Math.min(5, count)) : 1;
  for (let k = 0; k < n; k++) {
    const a = k / n * Math.PI * 2 + 0.4, d = n === 1 ? 0 : radius * 0.35;
    const base: V3 = [Math.cos(a) * d, floorY, Math.sin(a) * d];
    if (kind === 'led') {
      b.lathe(base, [[0.025, 0], [0.025, 0.012], [0.012, 0.016], [0.012, 0.03], [0, 0.034]], (k2) => k2 < 2 ? [0.02, 0.02, 0.02] : [1, 1, 1], MAT.glow, 10);
      at.push([base[0], floorY + 0.04, base[2]]);
    } else {
      const h = 0.03 + (k % 2) * 0.012 + (n === 1 ? 0.02 : 0);
      b.lathe(base, [[0.0, 0], [0.019, 0], [0.019, h], [0.016, h + 0.002], [0.0, h - 0.002]], () => [0.75, 0.68, 0.55], MAT.wax, 12);
      b.tube([[base[0], floorY + h - 0.003, base[2]], [base[0], floorY + h + 0.006, base[2]]], [0.0015, 0.001], () => [0.02, 0.02, 0.02], MAT.plain, 4);
      at.push([base[0], floorY + h + 0.018, base[2]]);
    }
  }
  return { at, ...b.data() };
}
